import React, {useEffect, useMemo, useState, useRef} from 'react';
import {User} from '../../../libs/types/src';
import {NoVNCConnection} from '../../../libs/ui/src';
import {CONSTANTS, TaskManagerService} from '../../../services';
import {Button, List, Skeleton, Spin} from 'antd';
import {CheckCircleOutlined, FrownOutlined} from '@ant-design/icons';

export function K8SAdminTools({
  users,
  client,
  getLoading,
  updateLoading,
}) {
  const [user, setUser] = useState(null);
  const connect = (u: User) => {
    setUser(null);
    setTimeout(() => setUser(u), 500);
  };
  const disconnect = () => setUser(null);
  return (
    <div>
      <h2>Kubernetes User Access</h2>
      <p>
        Below are the NoVNC connections for each active user in the Kubernetes environment.
      </p>
      <div>
        <UserList users={users} connect={connect} activeUser={user} disconnect={disconnect} />
        {user && (
          <NoVNCConnection
            client={client}
            user={user}
            options={{
              showToolbar: false,
              autoConnect: true,
              disabled: getLoading || updateLoading,
              isServerRunning: true,
            }}
            url={CONSTANTS.bot_grid_url}
          />
        )}
      </div>
    </div>
  )
}

function UserRow({user, connect, isRunning, disconnect}) {
  const [isReady, setIsReady] = useState(false);
  const [isReadyLoading, setIsReadyLoading] = useState(true);
  const isReadyRef = useRef(isReady);
  const [isLoading, setIsLoading] = useState(false);
  const launchServer = () => {
    if (isReady) {
      connect(user);
      return;
    }
    startServer();
  };
  const startServer = async () => {
    setIsLoading(true);
    const result = await TaskManagerService.startServer(user.id);
    const interval = setInterval(async () => {
      if (isReadyRef.current || result === 'invalid') {
        setIsLoading(false);
        clearInterval(interval);
      }
    }, 3 * 1000);
  }
  useEffect(() => {
    const checkStatus = async () => {
      setIsReadyLoading(true);
      const status = await TaskManagerService.getStatus(user.id);
      setIsReady(() => {
        const newIsReady = status === 'running';
        isReadyRef.current = newIsReady;
        return newIsReady;
      });
      setIsReadyLoading(false);
    }
    checkStatus();
    const interval = setInterval(checkStatus, 30 * 1000);
    return () => {
      clearInterval(interval);
    }
  }, []);
  return (
    <List.Item
      actions={[
        isRunning
          ? <Button variant="solid" color="danger" onClick={disconnect}>Disconnect</Button>
          : <Button type="primary" onClick={() => launchServer()} loading={isLoading} disabled={isLoading}>Launch server</Button>
      ]}
    >
      <Skeleton avatar title={false} loading={false} active>
        <List.Item.Meta
          avatar={null}
          title={`${user.first_name} ${user.last_name}`}
          description={user.email}
        />
        <div>
          {isReadyLoading && <Spin />}
          {!isReadyLoading && (
            <>
              {isReady
                ? <CheckCircleOutlined style={{color: "green", fontSize: 16}} />
                : <FrownOutlined style={{color: "red", fontSize: 16}} />
              }
              <span>{isReady ? ' ready' : ' offline'}</span>
            </>
          )}
        </div>
      </Skeleton>
    </List.Item>
  )
}

function UserList({users, connect, activeUser, disconnect}) {
  const filteredUsers = useMemo(() => (users.filter((user: User) => user.k8s_support)), [users]);

  return (
    <List
      itemLayout="horizontal"
      dataSource={filteredUsers}
      renderItem={(user: User) =>
        <UserRow
          user={user}
          connect={connect}
          isRunning={activeUser?.id === user.id}
          disconnect={disconnect}
        />
      }
    />
  )
}
